"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { Icon } from "@to/ui";

import type { OpenHouseMapItem } from "@/lib/data/open-houses";
import {
  MARKET_TIME_ZONE,
  dayVar,
  dayTint,
  nyWeekdayIndex,
  formatBedsBaths,
} from "@/lib/open-house-format";
import { formatPriceCompact } from "@/lib/format";
import { OpenHouseMapDynamic } from "@/components/open-house-map-dynamic";
import { DirectoryFilterTelemetry } from "@/components/analytics/DirectoryFilterTelemetry";
import { OpenHouseViewRow } from "@/components/analytics/OpenHouseViewRow";
import type { OpenHouseViewSurface } from "@/lib/open-house-view";

/**
 * Home "Open Houses — Next 7 Days" explorer (home v1 reskin) — the section BODY
 * the home page renders inside `<section id="open-houses">`. A re-tokenized port
 * of the v1 reference `open-house-section.tsx` (header pill, 7-day schedule
 * strip, summary stats, filter row, map/list toggle) keeping v1's layout and
 * filter SEMANTICS but the v2 styling system: every color via `--brand-*`
 * tokens (day palette `--brand-oh-day-0..6`), every glyph an `<Icon>`, and NO
 * animations / hover transforms — the approved mock is calm, so selection is a
 * plain border swap and nothing else moves.
 *
 * TIMEZONE PINNING (deliberate v1 bug-fix — do not regress): v1 bucketed with
 * UNPINNED `getHours()` / `getDay()`, so the same open house could land on a
 * different day card depending on the viewer's machine (a 7pm ET start is the
 * NEXT UTC day). Here EVERY date/time derivation — day-card keys, the
 * "today..today+6" strip, time-of-day buckets, list group headers, time badges —
 * runs through `Intl.DateTimeFormat` pinned to `America/New_York`, the SAME
 * market zone the read layer pins `dayOfWeek` to (see `lib/data/open-houses.ts`).
 * The strip anchors on `new Date()` read once per mount; server HTML and client
 * hydration can disagree only in the instant the NY date flips at midnight —
 * accepted (matches v1's exposure window).
 *
 * BASE vs FILTERED pipeline (v1 semantics):
 *   • `base`     = items ∩ price/type/beds/baths — ATTRIBUTE filters only.
 *                  Drives the strip's per-day counts, the headline count, and
 *                  the summary stats, so picking a day never changes the strip.
 *   • `filtered` = base ∩ selectedDate ∩ timeRange — drives BOTH map and list.
 *                  Time buckets (NY hours): morning <12, afternoon 12–15,
 *                  evening ≥16. Rows with no start time pass the time filter
 *                  (v1 behavior) but are excluded by a day pick.
 *
 * Property-type options are DERIVED from the data's distinct values — never
 * v1's hard-coded list. The map mounts ONLY via `OpenHouseMapDynamic` (the
 * `ssr:false` Leaflet boundary) and is replaced by the no-match empty state
 * when filters exclude everything. List rows: the property link wraps the row
 * content; the agent pill is a SEPARATE sibling link (`/agents/[slug]`) —
 * links are never nested. Locale pinned 'en-US' on every formatter.
 */

// ── NY-pinned formatters (module-level; 'en-US' everywhere) ─────────────────
// The zone comes from the shared open-house-format module — the SAME market
// zone the read layer pins `dayOfWeek` to.
const DATE_KEY_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: MARKET_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const HOUR_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: MARKET_TIME_ZONE,
  hour: "2-digit",
  hourCycle: "h23",
});
const WEEKDAY_SHORT_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: MARKET_TIME_ZONE,
  weekday: "short",
});
const MONTH_DAY_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: MARKET_TIME_ZONE,
  month: "short",
  day: "numeric",
});
const GROUP_DAY_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: MARKET_TIME_ZONE,
  weekday: "long",
  month: "short",
  day: "numeric",
});
const TIME_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: MARKET_TIME_ZONE,
  hour: "numeric",
  minute: "2-digit",
});

/** NY-calendar date key ("YYYY-MM-DD") for a Date. */
function dateKeyOf(d: Date): string {
  const parts = DATE_KEY_FMT.formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** NY-calendar date key for an ISO string; null when absent/invalid. */
function dateKeyNY(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return dateKeyOf(d);
}

/** NY hour-of-day (0..23, h23) for an ISO string; null when absent/invalid. */
function hourNY(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const part = HOUR_FMT.formatToParts(d).find((p) => p.type === "hour");
  return part ? Number(part.value) : null;
}

type TimeRange = "any" | "morning" | "afternoon" | "evening";

/** v1 buckets over NY hours: morning <12, afternoon 12–15, evening ≥16. */
function timeBucket(hour: number): Exclude<TimeRange, "any"> {
  if (hour < 12) return "morning";
  if (hour < 16) return "afternoon";
  return "evening";
}

/** v1 price buckets. "any" passes everything (incl. null prices). */
const PRICE_BUCKETS: {
  value: string;
  label: string;
  min: number;
  max: number | null;
}[] = [
  { value: "any", label: "Any Price", min: 0, max: null },
  { value: "0-250000", label: "Under $250K", min: 0, max: 250_000 },
  { value: "250000-500000", label: "$250K–$500K", min: 250_000, max: 500_000 },
  { value: "500000-750000", label: "$500K–$750K", min: 500_000, max: 750_000 },
  { value: "750000-1000000", label: "$750K–$1M", min: 750_000, max: 1_000_000 },
  { value: "1000000-2000000", label: "$1M–$2M", min: 1_000_000, max: 2_000_000 },
  { value: "2000000-", label: "$2M+", min: 2_000_000, max: null },
];

// v1's compact price ($x.xM / $xK / full) now comes from the shared
// `formatPriceCompact` in lib/open-house-format (same bytes as the old copy).

/** "1:00 PM – 3:00 PM" in NY time; start-only when end absent; null when none. */
function formatTimeWindow(
  startAt: string | null,
  endAt: string | null,
): string | null {
  if (!startAt) return null;
  const start = new Date(startAt);
  if (Number.isNaN(start.getTime())) return null;
  const startStr = TIME_FMT.format(start);
  if (!endAt) return startStr;
  const end = new Date(endAt);
  if (Number.isNaN(end.getTime())) return startStr;
  return `${startStr} – ${TIME_FMT.format(end)}`;
}

// ── Shared token styles ──────────────────────────────────────────────────────
const SELECT_STYLE: CSSProperties = {
  backgroundColor: "var(--brand-card-bg)",
  borderColor: "var(--brand-input-border)",
  color: "var(--brand-text)",
};
// Every filter <select> in the row wears the same classes.
const SELECT_CLASS =
  "min-h-11 cursor-pointer rounded-lg border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)] focus-visible:ring-offset-2";
// Map/List toggle faces — ONE button body maps over both modes.
const VIEW_TOGGLE = {
  map: { icon: "map", label: "Map" },
  list: { icon: "list", label: "List" },
} as const;
const ACCENT_PILL_BG =
  "color-mix(in srgb, var(--brand-accent) 14%, var(--brand-card-bg))";
const ACCENT_PILL_TEXT =
  "color-mix(in srgb, var(--brand-accent) 45%, var(--brand-text))";
// Summary dots — one fixed token-derived tone per time bucket.
const DOT_MORNING =
  "color-mix(in srgb, var(--brand-accent) 55%, var(--brand-card-bg))";
const DOT_AFTERNOON =
  "color-mix(in srgb, var(--brand-oh-day-3) 70%, var(--brand-card-bg))";
const DOT_EVENING =
  "color-mix(in srgb, var(--brand-oh-day-4) 70%, var(--brand-card-bg))";

/** Shared calm empty-state card: the zero-data and no-filter-match states
 * differ only in their title + trailing content (sub-line vs Clear button). */
function EmptyCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-xl border px-6 py-16 text-center"
      style={{
        borderColor: "var(--brand-card-border)",
        backgroundColor: "var(--brand-card-bg)",
      }}
    >
      <span
        className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full"
        style={{
          backgroundColor: "var(--brand-surface-subtle)",
          color: "var(--brand-text-muted)",
        }}
      >
        <Icon name="event_busy" size={26} />
      </span>
      <p
        className="text-base font-medium"
        style={{
          color: "var(--brand-text)",
          fontFamily: "var(--brand-font-heading)",
        }}
      >
        {title}
      </p>
      {children}
    </div>
  );
}

export interface OpenHouseWeekExplorerProps {
  /** Mappable open houses for the next 7 days (page passes the cached fetch). */
  items: OpenHouseMapItem[];
  /** Org base path — links become `${basePath}/properties/..` / `${basePath}/agents/..`. */
  basePath: string;
  /** The homepage keeps h2 semantics; the dedicated route promotes this title. */
  headingLevel?: 1 | 2;
  /** Delay the homepage's offscreen map until it approaches the viewport. */
  deferMap?: boolean;
  orgIdentifier?: string;
  surface?: OpenHouseViewSurface;
}

/** An item plus its once-derived NY keys — see the enrichment memo below. */
type EnrichedItem = OpenHouseMapItem & {
  /** NY-calendar "YYYY-MM-DD" of startAt, or null (absent/invalid). */
  nyDateKey: string | null;
  /** NY hour-of-day (0..23) of startAt, or null (absent/invalid). */
  nyHour: number | null;
};

interface WeekDay {
  key: string; // NY "YYYY-MM-DD"
  dow: number; // 0=Sun..6=Sat (NY)
  dayLabel: string; // "Wed"
  dateLabel: string; // "Today" | "Tomorrow" | "Jun 12"
}

export function OpenHouseWeekExplorer({
  items,
  basePath,
  headingLevel = 2,
  deferMap = false,
  orgIdentifier,
  surface = "home_open_houses",
}: OpenHouseWeekExplorerProps) {
  // Strip anchor, read once per mount (see header note on the midnight window).
  const [now] = useState(() => new Date());

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>("any");
  const [priceRange, setPriceRange] = useState<string>("any");
  const [propertyType, setPropertyType] = useState<string>("all");
  const [beds, setBeds] = useState<number>(0);
  const [baths, setBaths] = useState<number>(0);
  // "map" unconditionally: when `items` is empty the component early-returns
  // the empty card BEFORE any view renders, so the initial mode is only ever
  // observed with items present.
  const [view, setView] = useState<"map" | "list">("map");
  const listTarget = useRef<HTMLDivElement>(null);
  const focusList = useRef(false);
  useEffect(() => {
    if (view === "list" && focusList.current) {
      listTarget.current?.focus();
      focusList.current = false;
    }
  }, [view]);

  // today..today+6 through the NY formatters (NOT local getDate()).
  const weekDays = useMemo<WeekDay[]>(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(now.getTime() + i * 86_400_000);
        return {
          key: dateKeyOf(d),
          // Shared NY weekday derivation; `d` is always valid → ?? 0 is inert.
          dow: nyWeekdayIndex(d.toISOString()) ?? 0,
          dayLabel: WEEKDAY_SHORT_FMT.format(d),
          dateLabel:
            i === 0 ? "Today" : i === 1 ? "Tomorrow" : MONTH_DAY_FMT.format(d),
        };
      }),
    [now],
  );

  // ONE enrichment pass: four downstream memos (dayCounts/stats/filtered/
  // grouped) need the NY date key and/or NY hour per item — derive both once
  // per item per data load instead of re-running the Intl formatters in each.
  const enriched = useMemo<EnrichedItem[]>(
    () =>
      items.map((i) => ({
        ...i,
        nyDateKey: dateKeyNY(i.startAt),
        nyHour: hourNY(i.startAt),
      })),
    [items],
  );

  // Distinct non-null property types, alphabetical — DERIVED, never hard-coded.
  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const i of items) if (i.propertyType) set.add(i.propertyType);
    return [...set].sort((a, b) => a.localeCompare(b, "en-US"));
  }, [items]);

  // `base`: attribute filters only (price/type/beds/baths) — see header.
  const base = useMemo(() => {
    const bucket = PRICE_BUCKETS.find((b) => b.value === priceRange);
    return enriched.filter((i) => {
      if (bucket && bucket.value !== "any") {
        if (i.price == null || i.price < bucket.min) return false;
        if (bucket.max != null && i.price >= bucket.max) return false;
      }
      if (propertyType !== "all" && i.propertyType !== propertyType)
        return false;
      if (beds > 0 && (i.beds == null || i.beds < beds)) return false;
      if (baths > 0 && (i.baths == null || i.baths < baths)) return false;
      return true;
    });
  }, [enriched, priceRange, propertyType, beds, baths]);

  // Per-NY-date counts of `base` → strip card counts + blob heights.
  const dayCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const i of base) {
      if (i.nyDateKey) counts.set(i.nyDateKey, (counts.get(i.nyDateKey) ?? 0) + 1);
    }
    return counts;
  }, [base]);

  // O(7) — plain math each render, no memo ceremony needed.
  const weekMax = Math.max(1, ...weekDays.map((d) => dayCounts.get(d.key) ?? 0));

  // Summary stats over `base`: time-bucket counts, price min–max, top-3 types.
  const stats = useMemo(() => {
    let morning = 0;
    let afternoon = 0;
    let evening = 0;
    let priceMin: number | null = null;
    let priceMax: number | null = null;
    const typeCounts = new Map<string, number>();
    for (const i of base) {
      const hour = i.nyHour;
      if (hour != null) {
        const b = timeBucket(hour);
        if (b === "morning") morning++;
        else if (b === "afternoon") afternoon++;
        else evening++;
      }
      if (i.price != null) {
        priceMin = priceMin == null || i.price < priceMin ? i.price : priceMin;
        priceMax = priceMax == null || i.price > priceMax ? i.price : priceMax;
      }
      if (i.propertyType) {
        typeCounts.set(i.propertyType, (typeCounts.get(i.propertyType) ?? 0) + 1);
      }
    }
    const topTypes = [...typeCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
    return { morning, afternoon, evening, priceMin, priceMax, topTypes };
  }, [base]);

  // `filtered`: base further narrowed by day + time bucket → map AND list.
  const filtered = useMemo(
    () =>
      base.filter((i) => {
        if (selectedDate && i.nyDateKey !== selectedDate) return false;
        if (timeRange !== "any") {
          if (i.nyHour != null && timeBucket(i.nyHour) !== timeRange)
            return false;
        }
        return true;
      }),
    [base, selectedDate, timeRange],
  );

  const filterTelemetry = <DirectoryFilterTelemetry orgIdentifier={orgIdentifier}
    surface="open_house_explorer" routeTemplate={surface === "open_houses_page" ? "/open-houses" : "/"}
    activeFilters={{ date: selectedDate ?? "", time_range: timeRange === "any" ? "" : timeRange,
      price_range: priceRange === "any" ? "" : priceRange, property_type: propertyType === "all" ? "" : propertyType, beds_min: beds, baths_min: baths }}
    resultCount={filtered.length} view={view} />;

  // List view: filtered grouped by NY date key, ascending (unknown dates last).
  const grouped = useMemo(() => {
    const groups = new Map<string, EnrichedItem[]>();
    for (const i of filtered) {
      const key = i.nyDateKey ?? "~unknown";
      const arr = groups.get(key);
      if (arr) arr.push(i);
      else groups.set(key, [i]);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  function clearFilters() {
    setSelectedDate(null);
    setTimeRange("any");
    setPriceRange("any");
    setPropertyType("all");
    setBeds(0);
    setBaths(0);
  }

  function groupLabel(key: string, rows: OpenHouseMapItem[]): string {
    const first = rows[0]?.startAt;
    if (!first) return "Date to be announced";
    const dayText = GROUP_DAY_FMT.format(new Date(first));
    if (key === weekDays[0]?.key) return `Today — ${dayText}`;
    if (key === weekDays[1]?.key) return `Tomorrow — ${dayText}`;
    return dayText;
  }

  // ── Header (always shown) ──────────────────────────────────────────────────
  const Heading = headingLevel === 1 ? "h1" : "h2";
  const header = (
    <div className="mb-8 text-center">
      <span
        className="mb-4 inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-medium"
        style={{
          backgroundColor: ACCENT_PILL_BG,
          color: ACCENT_PILL_TEXT,
          fontSize: "12px",
        }}
      >
        <Icon name="event" size={14} />
        Next 7 Days
      </span>
      <Heading
        className="text-2xl font-bold tracking-tight sm:text-3xl"
        style={{
          color: "var(--brand-text)",
          fontFamily: "var(--brand-font-heading)",
        }}
      >
        Open Houses — Next 7 Days
      </Heading>
      {items.length > 0 && (
        <p
          className="mx-auto mt-2 max-w-xl text-sm sm:text-base"
          style={{ color: "var(--brand-text-muted)" }}
        >
          {base.length.toLocaleString("en-US")} open{" "}
          {base.length === 1 ? "house" : "houses"} across the next 7 days. Click
          a day to explore.
        </p>
      )}
    </div>
  );

  // ── Empty (no data at all): calm card, no strip/filters/map ───────────────
  if (items.length === 0) {
    return (
      <div>
        {filterTelemetry}
        {header}
        <EmptyCard title="No open houses scheduled in the next 7 days">
          <p className="mt-1 text-sm" style={{ color: "var(--brand-text-muted)" }}>
            Check back soon for newly scheduled open houses.
          </p>
        </EmptyCard>
      </div>
    );
  }

  const noMatch = filtered.length === 0;

  return (
    <div>
      {filterTelemetry}
      {header}

      <div className="flex flex-col gap-4">
        {/* ── 7-day schedule card ─────────────────────────────────────────── */}
        <div
          className="rounded-xl border p-4"
          style={{
            borderColor: "var(--brand-card-border)",
            backgroundColor: "var(--brand-card-bg)",
          }}
        >
          <p
            className="mb-3 font-semibold uppercase tracking-wider"
            style={{ color: "var(--brand-text-muted)", fontSize: "11px" }}
          >
            7-day schedule
          </p>

          <div
            className="flex gap-2 overflow-x-auto pb-1 sm:grid sm:grid-cols-7 sm:overflow-visible sm:pb-0"
            role="group"
            aria-label="Filter open houses by day"
          >
            {weekDays.map((day) => {
              const count = dayCounts.get(day.key) ?? 0;
              const isSelected = selectedDate === day.key;
              const dayColor = dayVar(day.dow);
              // Linear 14–44px against the week max; min height when 0.
              const blobHeight = Math.max(
                14,
                Math.round((count / weekMax) * 44),
              );
              return (
                <button
                  key={day.key}
                  type="button"
                  aria-pressed={isSelected}
                  aria-label={`${day.dayLabel} ${day.dateLabel}: ${count.toLocaleString("en-US")} ${count === 1 ? "home" : "homes"}`}
                  onClick={() =>
                    setSelectedDate(isSelected ? null : day.key)
                  }
                  className="flex min-w-[84px] shrink-0 flex-col items-center gap-1.5 rounded-lg border px-2 py-3 sm:min-w-0 sm:shrink"
                  style={{
                    borderColor: isSelected
                      ? dayColor
                      : "var(--brand-card-border)",
                    backgroundColor: "var(--brand-card-bg)",
                  }}
                >
                  <span
                    className="font-semibold uppercase tracking-wider"
                    style={{ color: dayColor, fontSize: "10px" }}
                  >
                    {day.dayLabel}
                  </span>
                  <span
                    className="whitespace-nowrap text-xs font-medium"
                    style={{ color: "var(--brand-text)" }}
                  >
                    {day.dateLabel}
                  </span>
                  <span aria-hidden="true" className="flex h-11 items-end">
                    <span
                      className="block rounded-full"
                      style={{
                        width: "40px",
                        height: `${blobHeight}px`,
                        // Shared day tint — the SAME formula the map pins use.
                        backgroundColor: dayTint(day.dow),
                      }}
                    />
                  </span>
                  <span
                    className="text-lg font-semibold tabular-nums"
                    style={{ color: "var(--brand-text)" }}
                  >
                    {count.toLocaleString("en-US")}
                  </span>
                  <span
                    style={{ color: "var(--brand-text-muted)", fontSize: "11px" }}
                  >
                    {count === 1 ? "home" : "homes"}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Summary stats over `base` (left-aligned, wraps; no border-top). */}
          {base.length > 0 && (
            <div
              className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5"
              style={{ color: "var(--brand-text-muted)", fontSize: "11px" }}
            >
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: DOT_MORNING }}
                />
                {stats.morning.toLocaleString("en-US")} morning
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: DOT_AFTERNOON }}
                />
                {stats.afternoon.toLocaleString("en-US")} afternoon
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: DOT_EVENING }}
                />
                {stats.evening.toLocaleString("en-US")} evening
              </span>
              {stats.priceMin != null && stats.priceMax != null && (
                <>
                  <span aria-hidden="true">|</span>
                  <span>
                    {formatPriceCompact(stats.priceMin)} –{" "}
                    {formatPriceCompact(stats.priceMax)}
                  </span>
                </>
              )}
              {stats.topTypes.map(([type, count]) => (
                <span
                  key={type}
                  className="rounded-full px-2 py-0.5"
                  style={{ backgroundColor: "var(--brand-surface-subtle)" }}
                >
                  {type} ({count.toLocaleString("en-US")})
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── Filter row + map/list toggle ────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            aria-label="Time of day"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as TimeRange)}
            className={SELECT_CLASS}
            style={SELECT_STYLE}
          >
            <option value="any">Any Time</option>
            <option value="morning">Morning (before 12pm)</option>
            <option value="afternoon">Afternoon (12pm-4pm)</option>
            <option value="evening">Evening (after 4pm)</option>
          </select>

          <select
            aria-label="Price range"
            value={priceRange}
            onChange={(e) => setPriceRange(e.target.value)}
            className={SELECT_CLASS}
            style={SELECT_STYLE}
          >
            {PRICE_BUCKETS.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>

          <select
            aria-label="Property type"
            value={propertyType}
            onChange={(e) => setPropertyType(e.target.value)}
            className={SELECT_CLASS}
            style={SELECT_STYLE}
          >
            <option value="all">All Types</option>
            {typeOptions.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

          {/* Beds & baths are the same min-count select — mapped from config
              (placeholder "Beds"/"Baths", options "1+ Bed".."N+ Beds"). */}
          {(
            [
              { label: "Minimum beds", unitWord: "Bed", max: 5, value: beds, set: setBeds },
              { label: "Minimum baths", unitWord: "Bath", max: 4, value: baths, set: setBaths },
            ] as const
          ).map((cfg) => (
            <select
              key={cfg.label}
              aria-label={cfg.label}
              value={String(cfg.value)}
              onChange={(e) => cfg.set(Number(e.target.value))}
              className={SELECT_CLASS}
              style={SELECT_STYLE}
            >
              <option value="0">{`${cfg.unitWord}s`}</option>
              {Array.from({ length: cfg.max }, (_, i) => i + 1).map((n) => (
                <option key={n} value={String(n)}>
                  {n}+ {n === 1 ? cfg.unitWord : `${cfg.unitWord}s`}
                </option>
              ))}
            </select>
          ))}

          <div
            className="ml-auto inline-flex items-center gap-1 rounded-lg p-1"
            style={{ backgroundColor: "var(--brand-surface-subtle)" }}
            role="group"
            aria-label="Open house view"
          >
            {(["map", "list"] as const).map((mode) => {
              const active = view === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={active}
                  aria-label={`${VIEW_TOGGLE[mode].label} view`}
                  onClick={() => setView(mode)}
                  className="inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)] focus-visible:ring-offset-2"
                  style={
                    active
                      ? {
                          backgroundColor: "var(--brand-card-bg)",
                          borderColor: "var(--brand-card-border)",
                          color: "var(--brand-text)",
                        }
                      : {
                          backgroundColor: "transparent",
                          borderColor: "transparent",
                          color: "var(--brand-text-muted)",
                        }
                  }
                >
                  <Icon name={VIEW_TOGGLE[mode].icon} size={16} />
                  {VIEW_TOGGLE[mode].label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── View area: no-match state OR map OR grouped list ────────────── */}
        {noMatch ? (
          <EmptyCard title="No open houses match your filters">
            <button
              type="button"
              onClick={clearFilters}
              className="mt-4 rounded-lg border px-4 py-2 text-sm font-medium"
              style={{
                borderColor: "var(--brand-card-border)",
                backgroundColor: "var(--brand-card-bg)",
                color: "var(--brand-text)",
              }}
            >
              Clear filters
            </button>
          </EmptyCard>
        ) : view === "map" ? (
          <div>
          <button type="button" className="mb-2 min-h-11 rounded-lg border px-4 py-2 text-sm font-medium" onClick={() => { focusList.current = true; setView("list"); }}>
            Skip map and show List view
          </button>
          <div
            className="h-[500px] overflow-hidden rounded-xl border sm:h-[650px]"
            style={{ borderColor: "var(--brand-card-border)" }}
          >
            <OpenHouseMapDynamic
              orgIdentifier={orgIdentifier}
              surface={surface}
              items={filtered}
              basePath={basePath}
              deferUntilVisible={deferMap}
            />
          </div>
          </div>
        ) : (
          <div
            ref={listTarget} tabIndex={-1} aria-label="Open house listings"
            className="max-h-[600px] overflow-y-auto rounded-xl border"
            style={{
              borderColor: "var(--brand-card-border)",
              backgroundColor: "var(--brand-card-bg)",
            }}
          >
            {grouped.map(([key, rows]) => (
              <div key={key}>
                <div
                  className="sticky top-0 z-10 flex items-center justify-between border-b px-4 py-2"
                  style={{
                    backgroundColor: "var(--brand-card-bg)",
                    borderColor: "var(--brand-card-border)",
                  }}
                >
                  <h3
                    className="text-sm font-semibold"
                    style={{
                      color: "var(--brand-text)",
                      fontFamily: "var(--brand-font-heading)",
                    }}
                  >
                    {groupLabel(key, rows)}
                  </h3>
                  <span
                    className="text-xs"
                    style={{ color: "var(--brand-text-muted)" }}
                  >
                    {rows.length.toLocaleString("en-US")}{" "}
                    {rows.length === 1 ? "home" : "homes"}
                  </span>
                </div>

                {rows.map((oh) => {
                  const timeWindow = formatTimeWindow(oh.startAt, oh.endAt);
                  const bedsBaths = formatBedsBaths(oh.beds, oh.baths);
                  return (
                    <OpenHouseViewRow
                      key={oh.id}
                      orgIdentifier={orgIdentifier} openHouseId={oh.id} listingId={oh.listingId} basePath={basePath} surface={surface}
                      className="flex items-center gap-3 border-b px-4 py-3 last:border-b-0"
                      style={{ borderColor: "var(--brand-card-border)" }}
                    >
                      {/* Main link wraps everything EXCEPT the agent pill. */}
                      <Link
                        href={`${basePath}/properties/${oh.mlsListingKey}`}
                        className="flex min-w-0 flex-1 items-center gap-4"
                      >
                        <span
                          className="block h-16 w-20 shrink-0 overflow-hidden rounded-lg"
                          style={{
                            backgroundColor: "var(--brand-surface-subtle)",
                          }}
                        >
                          {oh.photoUrl ? (
                            <Image
                              src={oh.photoUrl}
                              alt={oh.address ?? "Open house photo"}
                              width={80}
                              height={64}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span
                              className="flex h-full w-full items-center justify-center"
                              style={{ color: "var(--brand-text-muted)" }}
                            >
                              <Icon name="home" size={22} />
                            </span>
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            className="block truncate text-sm font-medium"
                            style={{ color: "var(--brand-text)" }}
                          >
                            {oh.address ?? "Address unavailable"}
                          </span>
                          {oh.city && (
                            <span
                              className="block text-xs"
                              style={{ color: "var(--brand-text-muted)" }}
                            >
                              {oh.city}
                            </span>
                          )}
                          <span
                            className="mt-1 flex flex-wrap items-center gap-x-3 text-xs"
                            style={{ color: "var(--brand-text-muted)" }}
                          >
                            {oh.price != null && (
                              <span
                                className="font-semibold"
                                style={{ color: "var(--brand-text)" }}
                              >
                                {formatPriceCompact(oh.price)}
                              </span>
                            )}
                            {bedsBaths && <span>{bedsBaths}</span>}
                          </span>
                        </span>
                        {timeWindow && (
                          <span
                            className="shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium"
                            style={{
                              backgroundColor: ACCENT_PILL_BG,
                              color: ACCENT_PILL_TEXT,
                            }}
                          >
                            {timeWindow}
                          </span>
                        )}
                      </Link>

                      {/* Agent pill — SEPARATE sibling link, never nested. */}
                      {oh.agentSlug && oh.agentName && (
                        <Link
                          href={`${basePath}/agents/${oh.agentSlug}`}
                          className="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium"
                          style={{
                            backgroundColor: "var(--brand-surface-subtle)",
                            color: "var(--brand-text)",
                          }}
                        >
                          <Icon name="person" size={14} />
                          <span className="max-w-[120px] truncate">
                            {oh.agentName}
                          </span>
                        </Link>
                      )}
                    </OpenHouseViewRow>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
